/* Advisor request modal — "Want a Clinq advisor to help you build this?"
   Shown at the start of /pitch/new and /build/new (financial model wizard). */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, PaperPlaneTilt, ShieldCheck, ChatCircleText } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { extractErrorMessage } from "@/lib/apiError";

const C = {
  panel: "#0A2826", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B",
};

const SERVICE_COPY = {
  pitch_deck: { title: "pitch deck", noun: "deck" },
  financial_model: { title: "financial model", noun: "model" },
  mgmt_accounts: { title: "management accounts", noun: "accounts" },
  business_valuation: { title: "business valuation", noun: "valuation" },
  general: { title: "fundraising documents", noun: "documents" },
};

export default function AdvisorModal({ service, onClose }) {
  const { user } = useAuth() || {};
  const copy = SERVICE_COPY[service] || SERVICE_COPY.pitch_deck;
  const [step, setStep] = useState("ask");  // ask | form | sent
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/advisor/request`, {
        ...form,
        service,
      });
      setStep("sent");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not send — try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.78)", fontFamily: "Manrope, sans-serif" }}
      data-testid="advisor-modal"
      onClick={onClose}>
      <div className="rounded-2xl max-w-lg w-full p-8 relative border text-white"
        style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.2)" }}
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="advisor-close"
          className="absolute top-4 right-4 text-white/40 hover:text-white">
          <X size={20} />
        </button>

        {step === "ask" && (
          <div data-testid="advisor-step-ask">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold mb-3"
              style={{ color: C.orange }}>
              <ChatCircleText size={12} weight="fill" /> Before you start
            </div>
            <h3 className="font-extrabold text-3xl tracking-tight leading-tight"
              style={{ letterSpacing: "-0.02em" }}>
              Want a Clinq <span style={{ color: C.teal }}>advisor</span> to help you build this?
            </h3>
            <p className="text-white/65 mt-3 text-sm leading-relaxed">
              Founders who work with an advisor close their round <strong className="text-white">significantly faster</strong>.
              We&apos;ll pair you with a Clinq partner who&apos;ll walk you through your {copy.title} — no extra cost on this round.
            </p>

            <div className="grid grid-cols-3 gap-3 mt-6">
              <Benefit label="30-min intro call" />
              <Benefit label="Founder-friendly" />
              <Benefit label="Reply &lt; 24h" />
            </div>

            <div className="flex items-center gap-2 mt-7">
              <button onClick={() => setStep("form")} data-testid="advisor-yes"
                className="flex-1 px-4 py-3 rounded-md font-semibold text-sm inline-flex items-center justify-center gap-1.5"
                style={{ backgroundColor: C.teal, color: "#fff" }}>
                Yes, connect me <PaperPlaneTilt size={14} weight="bold" />
              </button>
              <button onClick={onClose} data-testid="advisor-no"
                className="px-4 py-3 rounded-md font-semibold text-sm hover:bg-white/5"
                style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.65)" }}>
                I&apos;ll keep building
              </button>
            </div>
          </div>
        )}

        {step === "form" && (
          <form onSubmit={submit} data-testid="advisor-step-form">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold mb-3"
              style={{ color: C.orange }}>
              <ChatCircleText size={12} weight="fill" /> Talk to a Clinq advisor
            </div>
            <h3 className="font-extrabold text-2xl tracking-tight leading-tight"
              style={{ letterSpacing: "-0.02em" }}>
              Two minutes. We&apos;ll reply within <span style={{ color: C.teal }}>one business day</span>.
            </h3>

            {/* First-session-free pricing notice */}
            <div className="mt-4 rounded-md p-3 flex items-start gap-2.5"
              style={{ backgroundColor: "rgba(245, 158, 11, 0.08)", border: "1px solid rgba(245, 158, 11, 0.25)" }}
              data-testid="advisor-pricing-note">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded mt-0.5"
                style={{ backgroundColor: C.orange, color: "#1a1206" }}>
                Free
              </div>
              <div className="text-[12px] leading-snug text-white/75">
                <span className="font-semibold text-white">Your first session is on us.</span>{" "}
                Follow-on sessions are <span className="font-semibold text-white">KES 12,000 per 75&nbsp;minutes</span>.
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <Field label="Your name" required>
                <input type="text" required value={form.name} onChange={(e) => set("name", e.target.value)}
                  placeholder="Jane Mwangi" data-testid="advisor-name"
                  className="w-full px-3 py-2.5 rounded-md text-sm bg-black/30 border border-white/15 text-white focus:outline-none focus:border-[#0A8B85]" />
              </Field>
              <Field label="Email" required>
                <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
                  placeholder="jane@yourbusiness.co" data-testid="advisor-email"
                  className="w-full px-3 py-2.5 rounded-md text-sm bg-black/30 border border-white/15 text-white focus:outline-none focus:border-[#0A8B85]" />
              </Field>
              <Field label="Phone (optional)">
                <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  placeholder="+254 7…" data-testid="advisor-phone"
                  className="w-full px-3 py-2.5 rounded-md text-sm bg-black/30 border border-white/15 text-white focus:outline-none focus:border-[#0A8B85]" />
              </Field>
              <Field label={`What do you need help with on your ${copy.title}?`}>
                <textarea rows="3" value={form.message} onChange={(e) => set("message", e.target.value)}
                  placeholder="e.g. Stuck on traction slide, or — never built a forecast before."
                  data-testid="advisor-message"
                  className="w-full px-3 py-2.5 rounded-md text-sm bg-black/30 border border-white/15 text-white focus:outline-none focus:border-[#0A8B85] resize-none" />
              </Field>
            </div>

            <div className="flex items-center gap-2 mt-6">
              <button type="submit" disabled={busy} data-testid="advisor-submit"
                className="flex-1 px-4 py-3 rounded-md font-semibold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                style={{ backgroundColor: C.teal, color: "#fff" }}>
                {busy ? "Sending…" : <>Send request <PaperPlaneTilt size={14} weight="bold" /></>}
              </button>
              <button type="button" onClick={() => setStep("ask")}
                className="px-4 py-3 rounded-md font-semibold text-sm hover:bg-white/5"
                style={{ border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.65)" }}>
                Back
              </button>
            </div>
          </form>
        )}

        {step === "sent" && (
          <div data-testid="advisor-step-sent" className="text-center py-2">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
              style={{ backgroundColor: "rgba(10, 139, 133, 0.18)" }}>
              <ShieldCheck size={28} weight="fill" style={{ color: C.teal }} />
            </div>
            <h3 className="font-extrabold text-2xl tracking-tight" style={{ letterSpacing: "-0.02em" }}>
              You&apos;ll hear from a Clinq advisor <span style={{ color: C.teal }}>within one business day</span>.
            </h3>
            <p className="text-white/65 mt-3 text-sm leading-relaxed max-w-sm mx-auto">
              We sent a confirmation to <span className="text-white/90 font-mono-num">{form.email}</span>. Keep building in the meantime — your advisor will pick up exactly where you leave off.
            </p>

            {/* Pricing reminder card */}
            <div className="mt-5 max-w-sm mx-auto rounded-lg p-4 text-left"
              style={{ backgroundColor: "rgba(10, 139, 133, 0.08)", border: "1px solid rgba(10, 139, 133, 0.22)" }}
              data-testid="advisor-sent-pricing">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2"
                style={{ color: C.tealSoft }}>
                What happens next
              </div>
              <div className="flex items-start gap-2.5 mb-2.5">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded shrink-0"
                  style={{ backgroundColor: C.orange, color: "#1a1206" }}>
                  Free
                </span>
                <div className="text-[12.5px] leading-snug text-white/85">
                  Your <span className="font-semibold text-white">first 30-min session</span> is complimentary.
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded shrink-0"
                  style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }}>
                  KES 12k
                </span>
                <div className="text-[12.5px] leading-snug text-white/75">
                  Follow-on sessions are <span className="font-semibold text-white">KES 12,000 per 75&nbsp;minutes</span>, billed after each call.
                </div>
              </div>
            </div>
            <button onClick={onClose} data-testid="advisor-sent-close"
              className="mt-6 px-5 py-3 rounded-md font-semibold text-sm"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              Keep building →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Benefit({ label }) {
  return (
    <div className="rounded-md p-2.5 text-center"
      style={{ backgroundColor: "rgba(10, 139, 133, 0.08)", border: "1px solid rgba(10, 139, 133, 0.18)" }}>
      <div className="text-[11px] font-bold leading-tight" style={{ color: C.tealSoft }}
        dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/45 mb-1.5">
        {label}{required && <span style={{ color: C.orange }}> *</span>}
      </div>
      {children}
    </div>
  );
}
